import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { AppRoot } from '../components/AppRoot'
import { ACTAS_ROSTER } from '../data/roster-config'
import {
  adminCreateInvite,
  adminRevokeInvite,
  adminSetBlock,
  adminSetStatus,
  fetchAllProfiles,
  fetchInvites,
} from '../lib/admin'
import type { Invite } from '../lib/admin'
import type { BlockCode, Profile } from '../lib/profile'
import { Button, SelectField, TextField } from '../ui'
import type { SelectOption } from '../ui'

// A leading empty option represents "unset" (a profile with no block_code yet), so a row with no
// current block doesn't silently default to the first real block in the dropdown — the `<select>`
// pins to this instead, and "Save block" stays disabled until an admin actually picks one.
const BLOCK_OPTIONS: SelectOption[] = [
  { value: '', label: 'Select a block…' },
  ...ACTAS_ROSTER.blockOrder.map((code) => ({ value: code, label: code })),
]

/**
 * `/admin` — rendered by `AppRoutes` (Task 12) only when `profile?.is_admin && profile?.status
 * === 'ACTIVE'`. Takes no props, same as `SetupScreen`: there is nothing for this screen to call
 * back to, and it does not repeat that `is_admin` check itself — every mutation below is
 * independently re-checked server-side by each `admin_*` function's own `is_admin()` guard
 * (`0003_audit_admin.sql`), so a client-side check here would be redundant, not defensive.
 *
 * Three independently loaded, independently refetched sections: mint an invite, the invite list,
 * the user list. Each keeps its own loading/error state; nothing here is optimistic — a
 * successful mutation just triggers a full refetch of the list it touched, which is fine for an
 * internal tool at this project's scale.
 */
export function AdminScreen(): ReactElement {
  // --- Mint an invite ------------------------------------------------------
  const [note, setNote] = useState('')
  const [minting, setMinting] = useState(false)
  const [mintError, setMintError] = useState<string | null>(null)
  const [mintedCode, setMintedCode] = useState<string | null>(null)

  // --- Invites ---------------------------------------------------------------
  const [invites, setInvites] = useState<Invite[]>([])
  const [invitesError, setInvitesError] = useState<string | null>(null)
  const [revokingCode, setRevokingCode] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<Record<string, string>>({})

  // --- Users -------------------------------------------------------------
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [profilesError, setProfilesError] = useState<string | null>(null)
  const [blockSelections, setBlockSelections] = useState<Record<string, string>>({})
  const [savingBlockFor, setSavingBlockFor] = useState<string | null>(null)
  const [blockError, setBlockError] = useState<Record<string, string>>({})
  const [togglingStatusFor, setTogglingStatusFor] = useState<string | null>(null)
  const [statusError, setStatusError] = useState<Record<string, string>>({})

  useEffect(() => {
    void loadInvites()
    void loadProfiles()
  }, [])

  async function loadInvites() {
    const { invites: fetched, error } = await fetchInvites()
    setInvites(fetched)
    setInvitesError(error ? error.message : null)
  }

  async function loadProfiles() {
    const { profiles: fetched, error } = await fetchAllProfiles()
    setProfiles(fetched)
    setProfilesError(error ? error.message : null)
  }

  async function handleMint() {
    setMinting(true)
    setMintError(null)
    setMintedCode(null)

    const { code, error } = await adminCreateInvite(note.trim() || null)

    if (error) {
      setMintError(error.message)
      setMinting(false)
      return
    }

    setMintedCode(code)
    setNote('')
    setMinting(false)
    await loadInvites()
  }

  async function handleRevoke(code: string) {
    setRevokingCode(code)
    setRevokeError((prev) => ({ ...prev, [code]: '' }))

    const { error } = await adminRevokeInvite(code)

    if (error) {
      setRevokeError((prev) => ({ ...prev, [code]: error.message }))
      setRevokingCode(null)
      return
    }

    setRevokingCode(null)
    await loadInvites()
  }

  async function handleSaveBlock(userId: string) {
    const selected = blockSelections[userId]
    if (!selected) return

    setSavingBlockFor(userId)
    setBlockError((prev) => ({ ...prev, [userId]: '' }))

    const { error } = await adminSetBlock(userId, selected as BlockCode)

    if (error) {
      setBlockError((prev) => ({ ...prev, [userId]: error.message }))
      setSavingBlockFor(null)
      return
    }

    setSavingBlockFor(null)
    await loadProfiles()
  }

  async function handleToggleStatus(userId: string, currentStatus: Profile['status']) {
    const nextStatus = currentStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'

    setTogglingStatusFor(userId)
    setStatusError((prev) => ({ ...prev, [userId]: '' }))

    const { error } = await adminSetStatus(userId, nextStatus)

    if (error) {
      setStatusError((prev) => ({ ...prev, [userId]: error.message }))
      setTogglingStatusFor(null)
      return
    }

    setTogglingStatusFor(null)
    await loadProfiles()
  }

  return (
    <AppRoot>
      <div className="sl-stack">
        <h1 className="sl-heading">Admin</h1>

        <section className="sl-stack">
          <h2 className="sl-heading">Mint an invite</h2>
          <TextField label="Note" value={note} onChange={setNote} hint="Optional — e.g. 'for Jane'" />
          <Button variant="primary" disabled={minting} onClick={() => void handleMint()}>
            Mint code
          </Button>
          {mintedCode ? (
            <p className="sl-hint">
              New code: <code>{mintedCode}</code>
            </p>
          ) : null}
          {mintError ? (
            <p className="sl-hint" role="alert">
              {mintError}
            </p>
          ) : null}
        </section>

        <section className="sl-stack">
          <h2 className="sl-heading">Invites</h2>
          {invitesError ? (
            <p className="sl-hint" role="alert">
              {invitesError}
            </p>
          ) : null}
          {invites.map((invite) => (
            <div key={invite.code} className="sl-stack">
              <p>
                <code>{invite.code}</code>
                {invite.note ? ` — ${invite.note}` : ''} — {invite.uses}/{invite.max_uses} used
                {invite.expires_at ? ` — expires ${invite.expires_at}` : ''}
                {invite.revoked ? ' — revoked' : ''}
              </p>
              {!invite.revoked ? (
                <Button
                  variant="secondary"
                  disabled={revokingCode === invite.code}
                  onClick={() => void handleRevoke(invite.code)}
                >
                  Revoke
                </Button>
              ) : null}
              {revokeError[invite.code] ? (
                <p className="sl-hint" role="alert">
                  {revokeError[invite.code]}
                </p>
              ) : null}
            </div>
          ))}
        </section>

        <section className="sl-stack">
          <h2 className="sl-heading">Users</h2>
          {profilesError ? (
            <p className="sl-hint" role="alert">
              {profilesError}
            </p>
          ) : null}
          {profiles.map((profile) => (
            <div key={profile.id} className="sl-stack">
              <p>
                <code>{profile.id.slice(0, 8)}</code> — {profile.display_name ?? '(no name yet)'} —{' '}
                {profile.status} — {profile.block_code ?? '—'}
                {profile.is_admin ? ' — admin' : ''}
              </p>

              <SelectField
                label="Block"
                options={BLOCK_OPTIONS}
                value={blockSelections[profile.id] ?? profile.block_code ?? ''}
                onChange={(value) =>
                  setBlockSelections((prev) => ({ ...prev, [profile.id]: value }))
                }
              />
              <Button
                variant="secondary"
                disabled={
                  savingBlockFor === profile.id ||
                  (blockSelections[profile.id] ?? profile.block_code ?? '') === ''
                }
                onClick={() => void handleSaveBlock(profile.id)}
              >
                Save block
              </Button>
              {blockError[profile.id] ? (
                <p className="sl-hint" role="alert">
                  {blockError[profile.id]}
                </p>
              ) : null}

              {profile.status !== 'PENDING' ? (
                <Button
                  variant="secondary"
                  disabled={togglingStatusFor === profile.id}
                  onClick={() => void handleToggleStatus(profile.id, profile.status)}
                >
                  {profile.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                </Button>
              ) : null}
              {statusError[profile.id] ? (
                <p className="sl-hint" role="alert">
                  {statusError[profile.id]}
                </p>
              ) : null}
            </div>
          ))}
        </section>
      </div>
    </AppRoot>
  )
}
